import { CompanyType, permissions, WithPermission, WithRestriction } from '@utilities-one/api-sdk';

import { hasPermission } from '@/entities/user/utils/access';

export const routeAccess: WithRestriction<WithPermission<{ pathname: string; exact?: boolean }>>[] = [
  {
    pathname: '/dashboard',
    exact: true,
    permission: permissions.analytics.dashboards.mainDashboard.view,
  },
  {
    pathname: '/dashboard/osp',
    permission: permissions.analytics.dashboards.ospDashboard.view,
  },
  {
    pathname: '/clients',
    permission: 'clients',
  },
  {
    pathname: '/clients/[id]/update',
    permission: permissions.clients.update,
  },
  {
    pathname: '/clients/[id]/jobSpecifications',
    permission: permissions.clients.jobSpecifications.view,
  },
  {
    pathname: '/clients/[id]/jobSpecifications/create',
    permission: permissions.clients.jobSpecifications.create,
  },
  {
    pathname: '/clients/[id]/jobSpecifications/[id]/update',
    permission: permissions.clients.jobSpecifications.update,
  },
  {
    pathname: '/projects',
    permission: 'projects',
  },
  {
    pathname: '/projects/[id]/map',
    permission: 'projects.view',
  },
  {
    pathname: '/projectManagement/create',
    permission: permissions.projectManagement.create,
  },
  {
    pathname: '/projectManagement/[id]/update/[stage]',
    permission: permissions.projectManagement.update,
  },
  {
    pathname: '/projectManagement/[id]/update/revenueForecasts',
    permission: permissions.projectManagement.financialForecast.viewRevenue,
  },
  {
    pathname: '/projectManagement/[id]/update/laborForecasts',
    permission: permissions.projectManagement.financialForecast.viewLabor,
  },
  {
    pathname: '/projectManagement/[id]/update/overallCosts',
    permission: permissions.projectManagement.financialForecast.viewOverallCosts,
  },
  {
    pathname: '/projectManagement/[id]/update/scheduleExecution',
    permission: permissions.projectManagement.financialForecast.viewScheduleExecution,
  },
  {
    pathname: '/projectManagement/[id]/update/monthlyPL',
    permission: permissions.projectManagement.financialForecast.viewMonthlyPL,
  },
  {
    pathname: '/projects/[id]/reels',
    permission: permissions.projects.reels.view,
  },
  {
    pathname: '/reports/jobs',
    permission: 'jobs.view',
  },
  {
    pathname: '/jobSpecForecast',
    permission: permissions.forecasts.jobSpecificationsForecast.view,
  },
  {
    pathname: '/reports/clients-vendor-rate-cards',
    permission: 'analytics.reports.clientVendorRateCards',
  },
  {
    pathname: '/notifications',
    permission: 'notifications',
  },
  {
    pathname: '/users',
    permission: 'settings.users',
  },
  {
    pathname: '/users/create',
    permission: permissions.settings.users.create,
  },
  {
    pathname: '/users/history',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/users/[id]/details',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/users/[id]/update',
    permission: permissions.settings.users.update,
  },
  {
    pathname: '/users/[id]/history',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/roles',
    permission: 'settings.roles',
  },
  {
    pathname: '/roles/create',
    permission: permissions.settings.roles.create,
  },
  {
    pathname: '/roles/[id]/update',
    permission: permissions.settings.roles.update,
  },
  {
    pathname: '/sessions',
    permission: 'settings.sessions',
  },
  {
    pathname: '/system/env',
    permission: permissions.settings.system.env,
  },
  {
    pathname: '/companies',
    permission: 'companies',
  },
  {
    pathname: '/companies/create',
    permission: permissions.companies.create,
  },
  {
    pathname: '/companies/[id]/update',
    permission: permissions.companies.update,
  },
  {
    pathname: '/companies/[id]/clients',
    permission: permissions.companies.clients.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications',
    permission: permissions.companies.jobSpecifications.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications/[id]/update',
    permission: permissions.companies.jobSpecifications.update,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications/managePrices',
    permission: permissions.companies.jobSpecifications.update,
  },
  {
    pathname: '/damages',
    permission: permissions.damageDrafts.view,
  },
  {
    pathname: '/damages/create',
    permission: permissions.damageDrafts.create,
  },
  {
    pathname: '/damages/[damageId]/update',
    permission: permissions.damageDrafts.update,
  },
  {
    pathname: '/gigs',
    permission: 'gigs',
  },
  {
    pathname: '/restorations',
    permission: 'restorations',
  },
  {
    pathname: '/per-diem',
    permission: 'perDiem.view',
  },
  {
    pathname: '/per-diem',
    permission: 'perDiem.view',
  },
  {
    pathname: '/per-diem/create',
    permission: permissions.perDiem.create,
    restriction: (user) => user.company.type === CompanyType.inhouse,
  },
  {
    pathname: '/invoices/create',
    restriction: (user) =>
      hasPermission(user, permissions.userProfile.companyScoped) && user.company.type === CompanyType.externalVendor,
    permission: permissions.companyInvoices.create,
  },
  {
    pathname: '/invoices',
    permission: permissions.companyInvoices.view,
  },
  {
    pathname: '/invoices/[id]/details/invoice',
    permission: permissions.companyInvoices.view,
  },
  {
    pathname: '/invoices/[id]/details/creditMemo',
    permission: permissions.companyInvoices.view,
  },

  {
    pathname: '/check-in',
    permission: [permissions.dailyCheckIn.checkIn.viewAll, permissions.dailyCheckIn.checkIn.viewAsForeman],
  },
  {
    pathname: '/activities',
    permission: [permissions.dailyCheckIn.activities.viewAll, permissions.dailyCheckIn.activities.viewAsForeman],
  },
  {
    pathname: '/remittance',
    permission: [permissions.remittance.view],
  },
  {
    pathname: '/remittance/create',
    permission: permissions.remittance.create,
  },
  {
    pathname: '/profile/clients',
    permission: [permissions.clients.view, permissions.userProfile.clients.view],
  },
  {
    pathname: '/profile/clients/[clientId]/companyContracts',
    permission: [permissions.clients.view, permissions.userProfile.clients.view],
  },
  {
    pathname: '/velocity-forecast',
    permission: permissions.forecasts.velocityForecast.view,
  },
  {
    pathname: '/velocity-forecast/create',
    permission: permissions.forecasts.velocityForecast.manage,
  },
  {
    pathname: '/readinessForecast',
    permission: permissions.forecasts.readinessForecast.view,
  },
  {
    pathname: '/revenue-forecast',
    permission: 'analytics.reports.revenueForecast',
  },
  {
    pathname: '/reports/workable-forecast-report',
    permission: 'analytics.reports.workableForecastReport',
  },
  {
    pathname: '/reports/workforce-forecast',
    permission: 'analytics.reports.workforceForecast',
  },
  {
    pathname: '/revenue-report',
    permission: 'analytics.reports.revenueReport',
  },
  {
    pathname: '/reports/tti-report',
    permission: 'analytics.reports.timeToInvoice',
  },
  {
    pathname: '/cop',
    permission: permissions.cop.view,
  },
  {
    pathname: '/reports/tti-report',
    permission: permissions.analytics.reports.timeToInvoice,
  },
  {
    pathname: '/cop',
    permission: permissions.cop.view,
  },
  {
    pathname: '/drive',
    permission: permissions.drive.view,
  },
  {
    pathname: '/gpm-drive/camera',
    permission: permissions.media.takePhoto,
  },
  {
    pathname: '/gpm-drive/all-media',
    permission: permissions.media.view,
  },
  {
    pathname: '/gpm-drive/drive',
    permission: permissions.drive.view,
  },
  {
    pathname: '/media',
    permission: permissions.media.view,
  },
];
